'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Stethoscope, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDiagnoseApplePayPlatformDomain } from '@/lib/api/apple-pay-platform';

import type { PlatformDomain } from './types';

type Status = 'ok' | 'warn' | 'fail';

interface Diagnostic {
  id: string;
  status: Status;
  values?: Record<string, string | number>;
}

const STATUS_ICON: Record<Status, { Icon: typeof CheckCircle2; className: string }> = {
  ok: { Icon: CheckCircle2, className: 'text-emerald-600' },
  warn: { Icon: AlertTriangle, className: 'text-amber-600' },
  fail: { Icon: XCircle, className: 'text-destructive' },
};

/**
 * Runs every check that stands between a configured domain and a working Apple
 * Pay button, and says which one is failing.
 *
 * The funnel check is the one that cannot be inferred from anywhere else on this
 * page: `paymentSetupConfig` is baked when a funnel is saved, so a domain can be
 * registered, live, correctly served and carrying the right registration in the
 * database while shoppers are handed an older one.
 */
export function DiagnoseDomainDialog({ domain }: { domain: PlatformDomain }) {
  const { t } = useTranslation('manageApplePay');
  const [open, setOpen] = useState(false);
  const diagnose = useDiagnoseApplePayPlatformDomain();

  const run = () => {
    setOpen(true);
    diagnose.mutate({ domainId: domain.id });
  };

  const checks = (diagnose.data?.checks ?? []) as Diagnostic[];
  const failing = checks.filter((c) => c.status !== 'ok').length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Icon only: this sits on every row, and a labelled button crowded out
          the actions that change state. The label moves to a tooltip and to
          aria-label, so it stays reachable by hover and by screen reader. */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={run}
                aria-label={t('domains.actions.diagnose')}
              >
                <Stethoscope className="h-3.5 w-3.5" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('domains.actions.diagnose')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('diagnose.title', { domain: domain.domain })}</DialogTitle>
          <DialogDescription>
            {diagnose.isPending
              ? t('diagnose.running')
              : failing === 0 && checks.length > 0
                ? t('diagnose.allGood')
                : t('diagnose.description')}
          </DialogDescription>
        </DialogHeader>

        {diagnose.isPending ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : (
          <ul className="space-y-3">
            {checks.map((check) => {
              const { Icon, className } = STATUS_ICON[check.status];
              return (
                <li key={check.id} className="flex items-start gap-2">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${className}`} />
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-sm font-medium">{t(`diagnose.checks.${check.id}.title`)}</p>
                    <p className="text-muted-foreground text-xs [overflow-wrap:anywhere]">
                      {t(`diagnose.checks.${check.id}.${check.status}`, check.values ?? {})}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
